import React from 'react';
import { Shield, Database, Lock, Users, FileCheck, Bell } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import FloatingButtons from '../components/common/FloatingButtons';

const PrivacyPolicyPage = () => {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="relative h-[40vh] flex items-center justify-center"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/685a4a49-d889-47d7-87c9-a5d959d86d4d/images/96b248514777c3ed6f57168160495b7ad27975bb81fd50338dbb79edf9d616b6.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center text-white px-6">
          <Shield className="w-16 h-16 mx-auto mb-4 text-teal-400" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-lg text-gray-200">Your Data, Your Rights</p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose prose-lg max-w-none">
          
          {/* DPDP Notice */}
          <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mb-8 rounded-r-lg">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-teal-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-teal-800 font-semibold m-0 mb-2">Digital Personal Data Protection Act 2023</h3>
                <p className="text-teal-700 m-0">
                  This Privacy Policy is compliant with the Digital Personal Data Protection Act, 2023 (DPDP Act) of India. 
                  We are committed to protecting your personal data and respecting your privacy rights.
                </p>
              </div>
            </div>
          </div>

          {/* Key Points Grid */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            <div className="bg-gray-50 p-4 rounded-xl text-center">
              <Database className="w-8 h-8 text-teal-500 mx-auto mb-2" />
              <h4 className="font-semibold text-gray-900 text-sm">What We Collect</h4>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl text-center">
              <Lock className="w-8 h-8 text-teal-500 mx-auto mb-2" />
              <h4 className="font-semibold text-gray-900 text-sm">How We Protect</h4>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl text-center">
              <Users className="w-8 h-8 text-teal-500 mx-auto mb-2" />
              <h4 className="font-semibold text-gray-900 text-sm">Who We Share With</h4>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. What Data We Collect</h2>
          <p className="text-gray-600 mb-4">
            We collect the following types of personal data:
          </p>
          
          <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">A. Information You Provide</h3>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li><strong>Contact Information:</strong> Name, email address, phone number</li>
            <li><strong>Health Information:</strong> Symptoms, medical history (only when you choose to share for consultation purposes)</li>
            <li><strong>Consultation Details:</strong> Preferred date, time, and type of consultation</li>
            <li><strong>Communication Data:</strong> Messages and inquiries you send us</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">B. Information Collected Automatically</h3>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li><strong>Device Information:</strong> Browser type, operating system, device type</li>
            <li><strong>Usage Data:</strong> Pages visited, time spent on site, click patterns</li>
            <li><strong>IP Address:</strong> For security and analytics purposes</li>
            <li><strong>Cookies:</strong> To improve your browsing experience</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Why We Collect Your Data</h2>
          <p className="text-gray-600 mb-4">
            We collect and process your personal data for the following purposes:
          </p>
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <ul className="list-disc pl-6 text-gray-600 space-y-3 m-0">
              <li><strong>Service Delivery:</strong> To facilitate doctor consultations and appointment bookings</li>
              <li><strong>Communication:</strong> To respond to your inquiries and send appointment confirmations</li>
              <li><strong>Improvement:</strong> To enhance our website and services based on usage patterns</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
              <li><strong>Security:</strong> To protect our platform and users from fraudulent activities</li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. How We Store Your Data</h2>
          <div className="flex items-start gap-4 mb-6">
            <Lock className="w-8 h-8 text-teal-500 flex-shrink-0" />
            <div>
              <p className="text-gray-600 mb-4">
                We implement robust security measures to protect your personal data:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Encryption:</strong> All sensitive data is encrypted during transmission and storage</li>
                <li><strong>Secure Servers:</strong> Data is stored on secure, access-controlled servers</li>
                <li><strong>Access Control:</strong> Only authorized personnel can access personal data</li>
                <li><strong>Regular Audits:</strong> We conduct regular security assessments</li>
                <li><strong>Data Retention:</strong> We retain data only as long as necessary for the stated purposes</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. With Whom We Share Your Data</h2>
          <p className="text-gray-600 mb-4">
            We may share your personal data with:
          </p>
          <div className="space-y-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Medical Practitioners</h4>
              <p className="text-gray-600 text-sm m-0">
                When you book a consultation, relevant information is shared with the doctor to facilitate your appointment.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Service Providers</h4>
              <p className="text-gray-600 text-sm m-0">
                Third-party services that help us operate our platform (e.g., hosting, analytics) under strict confidentiality agreements.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Legal Authorities</h4>
              <p className="text-gray-600 text-sm m-0">
                When required by law or to protect our legal rights, we may disclose information to appropriate authorities.
              </p>
            </div>
          </div>
          <p className="text-gray-600 mb-6">
            <strong>We do NOT sell your personal data to third parties for marketing purposes.</strong>
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Your Consent</h2>
          <div className="bg-teal-50 p-6 rounded-lg mb-6">
            <div className="flex items-start gap-3">
              <FileCheck className="w-6 h-6 text-teal-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Consent Mechanism</h4>
                <p className="text-gray-600 m-0 mb-3">
                  Under the DPDP Act 2023, we obtain your consent before collecting and processing personal data:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 m-0">
                  <li>By submitting forms on our website, you consent to the collection of provided information</li>
                  <li>You may withdraw consent at any time by contacting us</li>
                  <li>Withdrawal of consent may affect our ability to provide certain services</li>
                  <li>We maintain records of consent as required by law</li>
                </ul>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Your Rights Under DPDP Act 2023</h2>
          <p className="text-gray-600 mb-4">
            As a data principal, you have the following rights:
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="border border-gray-200 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Right to Access</h4>
              <p className="text-gray-600 text-sm m-0">Request a summary of your personal data and processing activities</p>
            </div>
            <div className="border border-gray-200 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Right to Correction</h4>
              <p className="text-gray-600 text-sm m-0">Request correction of inaccurate or incomplete personal data</p>
            </div>
            <div className="border border-gray-200 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Right to Erasure</h4>
              <p className="text-gray-600 text-sm m-0">Request deletion of your personal data (subject to legal requirements)</p>
            </div>
            <div className="border border-gray-200 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Right to Grievance Redressal</h4>
              <p className="text-gray-600 text-sm m-0">File complaints regarding handling of your personal data</p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Cookies Policy</h2>
          <p className="text-gray-600 mb-6">
            Our website uses cookies to enhance your experience. Cookies are small text files stored on your device that help us:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>Remember your preferences</li>
            <li>Understand how you use our website</li>
            <li>Improve our services</li>
          </ul>
          <p className="text-gray-600 mb-6">
            You can control cookies through your browser settings. However, disabling cookies may affect website functionality.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Changes to This Policy</h2>
          <p className="text-gray-600 mb-6">
            We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date. 
            We encourage you to review this policy periodically.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Contact & Grievance Officer</h2>
          <div className="bg-gray-50 p-6 rounded-lg">
            <p className="text-gray-600 mb-4">
              For any privacy-related queries, concerns, or to exercise your rights, please contact our Grievance Officer:
            </p>
            <p className="text-gray-700 mb-2">
              <strong>Email:</strong> doktorgigi4@gmail.com
            </p>
            <p className="text-gray-700 mb-2">
              <strong>Response Time:</strong> We will respond to your request within 30 days as per DPDP Act requirements.
            </p>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-gray-500 text-sm">
              <strong>Last Updated:</strong> February 2026
            </p>
          </div>
        </div>
      </section>
    </div>
    <Footer />
    <FloatingButtons />
    </>
  );
};

export default PrivacyPolicyPage;
